import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Square,
  Button,
  Row,
  Column,
  Stack,
  Flex,
  Copy,
  Image,
} from '@grubhubprod/gh-cookbook-react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import { uniqBy } from '@grubhubprod/umami-library/dist/nodash/uniqBy';
import { createNewLocationObject } from 'src/components/enterprise/features/Locations/ManageLocation/utilities/createNewLocationObject.utils';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { ILocation } from 'src/types/Location';
import { sdk } from 'src/utilities/sdk';
import { Input } from 'src/components/Input/Input';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { Select } from 'src/components/Select/Select';
import { STATES_LIST } from 'src/constants/states';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { CookbookTextareaWrapper } from 'src/components/CookbookTextareaWrapper/CookbookTextareaWrapper';
import { useAddNewLocation } from 'src/components/enterprise/features/Locations/hooks/useAddNewLocation';
import { useUpdateLocation } from 'src/components/enterprise/features/Locations/hooks/useUpdateLocation';
import { sanitizeAddressForGeocoding } from 'src/utilities/sanitizeAddressForGeocoding';

export interface ILocationFormState {
  isSubmitting: boolean;
  isValid: boolean;
}

export interface IEnterpriseLocationFormProps {
  locationId?: string;
  corpId: string;
  onSuccess: () => void;
  existingFormData?: ILocation;
  onConfirmAddressCb?: (
    fields: ILocation,
    uniqueSuggestedGeoAddresses: Address[]
  ) => void;
  formStateCb: ({ isSubmitting, isValid }: ILocationFormState) => void;
}
export type FormFields = {
  name: string;
  street_address1: string;
  street_address2: string;
  cross_streets: string;
  address_locality: string;
  address_region: string;
  address_sublocality: string;
  postal_code: string;
  delivery_instructions: string;
  latitude: string;
  longitude: string;
  location_internal_id?: string;
};

const emptyFormValues: FormFields = {
  name: '',
  street_address1: '',
  street_address2: '',
  cross_streets: '',
  address_locality: '',
  address_sublocality: '',
  address_region: '',
  postal_code: '',
  delivery_instructions: '',
  latitude: '',
  longitude: '',
};

const MIN_ADDRESS_LENGTH = 3;
const DELIVERY_INSTRUCTIONS_MAX_LENGTH = 500;

export const EnterpriseLocationForm = forwardRef(
  (
    {
      locationId,
      corpId,
      onSuccess,
      existingFormData,
      onConfirmAddressCb,
      formStateCb,
    }: IEnterpriseLocationFormProps,
    ref
  ): JSX.Element => {
    const {
      control,
      register,
      handleSubmit,
      errors,
      formState,
      watch,
      setValue,
      getValues,
    } = useForm<FormFields>({
      mode: 'onChange',
      defaultValues: emptyFormValues,
    });
    register('latitude');
    register('longitude');
    const { isValid, isSubmitting } = formState;
    const addressDropdownRef = useRef(null);
    const [showAddressDropdown, setShowAddressDropdown] = useState(false);
    const debouncedAddress = useDebouncedValue(
      watch('street_address1', '') as string,
      200
    );
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [location, setLocation] = useState<ILocation>(null);
    const { corpData } = useAuth();
    const isLocationInternalIdEnabled = corpData?.locationInternalIdEnabled;
    const isEditing = !!locationId;

    const { addLocation } = useAddNewLocation({
      corpId,
      onSuccess,
    });

    const { updateExistingLocation } = useUpdateLocation({
      corpId,
      onSuccess,
    });

    useImperativeHandle(ref, () => ({
      submit(wasAddressConfirmed) {
        handleSubmit(() => onFormSubmit(getValues(), wasAddressConfirmed))();
      },
    }));

    useEffect(() => {
      formStateCb({ isSubmitting, isValid });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting, isValid]);

    useEffect(() => {
      if (locationId && !existingFormData) {
        getLocation();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, existingFormData]);

    useEffect(() => {
      if (existingFormData) {
        setLocationValuesInForm(existingFormData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingFormData]);

    useEffect(() => {
      if (!addressDropdownRef.current) {
        return;
      }

      const addressLength = debouncedAddress.length;

      if (addressLength >= MIN_ADDRESS_LENGTH && showAddressDropdown) {
        getAddressSuggestions();
        addressDropdownRef.current.open();
      } else {
        addressDropdownRef.current.close();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedAddress, showAddressDropdown]);

    async function getAddressSuggestions() {
      const sanitizedAddress = sanitizeAddressForGeocoding(debouncedAddress);
      const results = await sdk.rest.geocode.autocomplete(
        encodeURIComponent(sanitizedAddress)
      );

      setAddressSuggestions(results);
    }

    async function getLocation() {
      try {
        const data: ILocation = await sdk.http.get(
          `/corp-admin/${corpId}/locations/${locationId}`
        );
        setLocation(data);
        setLocationValuesInForm(data);
      } catch (error) {
        pushErrorFromAPI(error);
      }
    }

    function setLocationValuesInForm(values: ILocation) {
      setValue('name', values.name, {
        shouldValidate: true,
      });
      setValue('street_address1', values.street_address1, {
        shouldValidate: true,
      });
      setValue('street_address2', values.street_address2, {
        shouldValidate: true,
      });
      setValue('cross_streets', values.cross_streets, {
        shouldValidate: true,
      });
      setValue('address_locality', values.address_locality, {
        shouldValidate: true,
      });
      setValue('address_region', values.address_region, {
        shouldValidate: true,
      });
      setValue('postal_code', values.postal_code, {
        shouldValidate: true,
      });
      setValue('delivery_instructions', values.delivery_instructions ?? '', {
        shouldValidate: true,
      });

      setValue('latitude', values.latitude);
      setValue('longitude', values.longitude);

      if (isLocationInternalIdEnabled) {
        setValue('location_internal_id', values.location_internal_id);
      }
    }

    async function getFullAddress(address: string) {
      const sanitizedAddress = sanitizeAddressForGeocoding(address);
      const [fullAddress] = await sdk.rest.geocode.selectAddress(
        sanitizedAddress
      );

      if (fullAddress) {
        const {
          address_region: state,
          address_locality: city,
          address_sublocality: city2,
          street_address1: address1,
          street_address2: address2,
          postal_code: zipcode,
          latitude,
          longitude,
        } = fullAddress;

        setValue('street_address1', address1, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue('street_address2', address2, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue('address_locality', city || city2, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue('address_region', state, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue('postal_code', zipcode, {
          shouldDirty: true,
          shouldValidate: true,
        });

        setValue('latitude', latitude);
        setValue('longitude', longitude);
      }

      setShowAddressDropdown(false);
    }

    async function onFormSubmit(fields: FormFields, wasAddressConfirmed) {
      let suggestedAddress;
      if (!wasAddressConfirmed) {
        const addressString = `${fields.street_address1}, ${fields.address_locality}, ${fields.address_region}, ${fields.postal_code}`;
        const sanitizedAddress = sanitizeAddressForGeocoding(addressString);
        const suggestedGeoAddresses: Address[] =
          await sdk.rest.geocode.selectAddress(
            encodeURIComponent(sanitizedAddress)
          );

        const addresses = suggestedGeoAddresses;

        // WDA-26352 do not show up the selection modal for same repeated address
        const uniqueSuggestedGeoAddresses = uniqBy(
          addresses,
          (addresses) =>
            `${addresses.street_address1}, ${addresses.address_locality}, ${addresses.address_region}, ${addresses.postal_code}`
        );

        if (uniqueSuggestedGeoAddresses.length > 1) {
          onConfirmAddressCb(
            { ...location, ...fields },
            uniqueSuggestedGeoAddresses
          );
          return;
        }

        suggestedAddress = uniqueSuggestedGeoAddresses[0];
      }

      const newLocation: Partial<ILocation> = {
        ...createNewLocationObject(fields, suggestedAddress),
      };

      if (isEditing) {
        await updateExistingLocation(locationId, newLocation);
      } else {
        await addLocation(newLocation);
      }
    }

    return (
      <form>
        <Stack y="md">
          <Row gutter={isLocationInternalIdEnabled ? 12 : 0}>
            <Column
              phone={isLocationInternalIdEnabled ? 6 : 12}
              gutter={isLocationInternalIdEnabled ? 12 : 0}
            >
              <Input
                label="Location name *"
                name="name"
                maxLength={500}
                id="name"
                ref={register({
                  required: 'Please add a location name',
                  maxLength: {
                    value: 500,
                    message: 'Location name must be 500 characters or less',
                  },
                })}
                error={errors.name}
              />
            </Column>
            {isLocationInternalIdEnabled && (
              <Column
                phone={isLocationInternalIdEnabled ? 6 : 12}
                gutter={isLocationInternalIdEnabled ? 12 : 0}
              >
                <Input
                  label="Location Internal ID"
                  name="location_internal_id"
                  id="location_internal_id"
                  ref={register}
                  placeholder="e.g. 12345"
                />
              </Column>
            )}
          </Row>
        </Stack>
        <Row gutter={12}>
          <Column gutter={12} tablet={6}>
            <Stack y="md" position="relative">
              <Dropdown
                ref={addressDropdownRef}
                isFullWidth
                closeMenuOnClick={false}
                disabled
                onToggle={(toggle) => setShowAddressDropdown(toggle)}
                toggle={() => (
                  <Input
                    label="Address *"
                    name="street_address1"
                    id="street_address1"
                    ref={register({
                      required: 'Please add an address',
                      minLength: {
                        value: MIN_ADDRESS_LENGTH,
                        message: 'Please enter a valid address',
                      },
                      maxLength: {
                        value: 255,
                        message: 'Address must be 255 characters or less',
                      },
                    })}
                    error={errors.street_address1}
                    onChange={(e) => {
                      if (e.target.value.length >= MIN_ADDRESS_LENGTH)
                        setShowAddressDropdown(true);
                    }}
                    onFocus={() => setShowAddressDropdown(true)}
                  />
                )}
                menu={({ show }) =>
                  show && (
                    <Flex
                      bg="globals.white"
                      flexDirection="column"
                      data-testid="address-suggestions-container"
                      zIndex={10}
                      position="relative"
                      width="100%"
                    >
                      <ul className="locationForm_addressSuggestions">
                        {addressSuggestions.length > 0 ? (
                          addressSuggestions.map((address) => (
                            <li
                              key={address}
                              className="locationForm_addressSuggestion"
                              data-testid="address-suggestions-item"
                            >
                              <Button
                                variant="tertiary"
                                type="button"
                                className="locationForm_addressSuggestionBtn"
                                onClick={() => getFullAddress(address)}
                              >
                                <Copy variant="body" color="secondaryText">
                                  {address}
                                </Copy>
                              </Button>
                            </li>
                          ))
                        ) : (
                          <NoResultsMsg message="No results found." />
                        )}
                      </ul>
                      <Flex justifyContent="center">
                        <Square inset="x-sm">
                          <Image
                            src="https://media-cdn.grubhub.com/image/upload/v1612897160/corporate/powered-by-google.svg"
                            alt="Powered by Google"
                          />
                        </Square>
                      </Flex>
                    </Flex>
                  )
                }
              />
            </Stack>
          </Column>
          <Column gutter={12} tablet={6}>
            <Stack y="md">
              <Input
                label="Apt/ suite"
                name="street_address2"
                id="street_address2"
                ref={register({
                  maxLength: {
                    value: 255,
                    message: 'Apt/ suite must be 255 characters or less',
                  },
                })}
                error={errors.street_address2}
              />
            </Stack>
          </Column>
        </Row>
        <Row gutter={12}>
          <Column gutter={12} tablet={6}>
            <Stack y="md">
              <Input
                label="Cross street"
                name="cross_streets"
                id="cross_streets"
                ref={register({
                  maxLength: {
                    value: 255,
                    message: 'Cross street must be 255 characters or less',
                  },
                })}
                error={errors.cross_streets}
              />
            </Stack>
          </Column>
          <Column gutter={12} tablet={6}>
            <Stack y="md">
              <Input
                label="City *"
                name="address_locality"
                maxLength={60}
                id="address_locality"
                ref={register({
                  required: 'Please add a city',
                  maxLength: {
                    value: 60,
                    message: 'City must be 60 characters or less',
                  },
                })}
                error={errors.address_locality}
              />
            </Stack>
          </Column>
          <Column gutter={12} tablet={6}>
            <Stack y="md">
              <Select
                label="State *"
                name="address_region"
                id="address_region"
                error={errors.address_region}
                ref={register({ required: 'State is required' })}
              >
                <option value={null}> </option>
                {STATES_LIST.map((state) => (
                  <option key={state.abbreviation} value={state.abbreviation}>
                    {state.name}
                  </option>
                ))}
              </Select>
            </Stack>
          </Column>
          <Column gutter={12} tablet={6}>
            <Stack y="md">
              <Input
                label="Zip code *"
                name="postal_code"
                id="postal_code"
                type="tel"
                error={errors.postal_code}
                ref={register({
                  required: 'Please add a zip code',
                  pattern: {
                    value: /^[0-9]{5}$/,
                    message: 'Zip code should be 5 numbers',
                  },
                })}
              />
            </Stack>
          </Column>
          <Column gutter={12} tablet={12}>
            <Stack y="md">
              <CookbookTextareaWrapper
                control={control}
                error={errors.delivery_instructions}
                id="delivery_instructions"
                name="delivery_instructions"
                label="Delivery instructions"
                placeholder="Leave a note for your driver to ensure a smooth drop-off (e.g. Take the service elevator to the 3rd fl...)"
                rules={{
                  maxLength: {
                    value: DELIVERY_INSTRUCTIONS_MAX_LENGTH,
                    message: `Delivery instructions must be ${DELIVERY_INSTRUCTIONS_MAX_LENGTH} characters or less`,
                  },
                }}
                maxLength={DELIVERY_INSTRUCTIONS_MAX_LENGTH}
                minHeight={100}
                blockWidth
              />
            </Stack>
          </Column>
        </Row>
      </form>
    );
  }
);

EnterpriseLocationForm.displayName = 'EnterpriseLocationForm';
